/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';
import type { AnchorNavItem, PageNavItem } from '../navigationItems';
import { AnchorLinkItem } from '../NavLinkItems';
import { usePageItemClick } from '../usePageItemClick';
import { ChevronIcon } from './ChevronIcon';

const ANCHOR_MENU_HEADING_ID = 'nav-item-anchor-menu-heading';

/**
 * Grace period between the pointer leaving this item and the menu closing.
 * There is no gap to cross — the popout renders inside the wrapper, so it
 * inherits the hover — but a pointer clipping the corner of the item on its way
 * somewhere else should not flash the menu open and shut.
 */
const HOVER_CLOSE_DELAY_MS = 200;

const styles = cssMap({
	// Hosts the popout as well as the item, which is what `shouldRenderToParent`
	// needs to keep the two inside one hover region: the popout is rendered into
	// the trigger's parent, so making `row` the trigger lands the popout here
	// rather than outside the element the mouse handlers are on.
	wrapper: {
		display: 'inline-flex',
		position: 'relative',
	},
	// The trigger, so the popout aligns to the item's left edge rather than the
	// chevron's. Also carries the hover fill, so label and chevron highlight as
	// one control.
	row: {
		display: 'inline-flex',
		alignItems: 'center',
		position: 'relative',
		borderRadius: token('radius.medium'),
		'@media (hover: hover)': {
			'&:hover': {
				backgroundColor: token('color.background.neutral.subtle.hovered'),
			},
		},
	},
	// The current-page marker, spanning the label and the chevron so it underlines
	// the whole item. A border rather than a fill so it survives forced-colors
	// mode, which is how ADS Tabs draws its selected line too. The colour is
	// stated rather than inherited because this element sets no `color` of its
	// own, and it matches the active label; `color.border.selected` is #1868DB in
	// the light theme, only 3.6:1 against this bar's hardcoded #111213.
	rowActive: {
		'&::after': {
			content: '""',
			position: 'absolute',
			insetBlockEnd: 0,
			insetInlineStart: token('space.150'),
			insetInlineEnd: token('space.150'),
			height: 0,
			borderBlockEndWidth: token('border.width.selected'),
			borderBlockEndStyle: 'solid',
			borderBlockEndColor: '#FFFFFF',
		},
	},
	link: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		// The arrow's own leading margin is the gap to the label, so no trailing
		// inset here.
		paddingInlineEnd: 0,
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		// The fill lives on `row`, so only the label colour responds here. Scoped
		// to real hover so a tap does not leave the brighter colour behind.
		'@media (hover: hover)': {
			'&:hover': {
				color: '#FFFFFF',
			},
		},
	},
	linkActive: {
		color: '#FFFFFF',
	},
	chevron: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInlineStart: 0,
		paddingInlineEnd: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		cursor: 'pointer',
		'@media (hover: hover)': {
			'&:hover': {
				color: '#FFFFFF',
			},
		},
	},
	// Matches the arrow beside "Products" on atlassian.com: the artwork itself
	// (see `ChevronIcon`), the same 8px gap to the label, and the same
	// `transition: 0.25s` — a duration-only shorthand, so `ease` at a quarter
	// second is what that site actually ships.
	//
	// A wrapper is rotated rather than the `svg` because `line-height: 0` here is
	// what stops the inline SVG adding descender space to the row.
	chevronIcon: {
		display: 'block',
		lineHeight: 0,
		marginInlineStart: token('space.100'),
		transitionProperty: 'transform',
		transitionDuration: '250ms',
		transitionTimingFunction: 'ease',
		// Not `none`: the transition stays declared so the rotation is the same
		// single state change either way, just an imperceptible one.
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '1ms',
		},
	},
	chevronIconOpen: {
		transform: 'rotate(180deg)',
	},
});

export type NavItemAnchorMenuProps = PageNavItem & {
	/**
	 * The destination's sections. Required and never empty here, unlike the
	 * optional `anchors` on `PageNavItem`: a page with none renders a plain
	 * `NavItemLink` instead of this.
	 */
	anchors: readonly AnchorNavItem[];
	testId?: string;
};

/**
 * A page link that also discloses its destination's sections, used for "About"
 * on wide viewports. The sections belong to the page the link points at, so
 * hanging them off that link keeps the bar to one navigation cluster.
 *
 * Shown on every route, which is why the heading reads "Sections" rather than
 * "On this page": from `/cli` these are another page's sections, and the items
 * navigate there carrying the fragment so one click still lands correctly.
 *
 * Opens on hover and on keyboard focus, and the chevron is a real button, so
 * the menu is reachable without a pointer. The link itself still navigates.
 *
 * Only rendered inside `NavButtons`, which CSS-hides itself below the width
 * where `NavMenu` takes over, so it never competes with the collapsed menu.
 */
export const NavItemAnchorMenu = ({
	path,
	routeName,
	message,
	anchors,
	testId = 'nav-item-anchor-menu',
}: NavItemAnchorMenuProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { isActive, onClick } = usePageItemClick(path, routeName);
	const [isOpen, setIsOpen] = useState(false);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const cancelScheduledClose = useCallback((): void => {
		if (closeTimerRef.current !== undefined) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = undefined;
		}
	}, []);

	const open = useCallback((): void => {
		cancelScheduledClose();
		setIsOpen(true);
	}, [cancelScheduledClose]);

	const close = useCallback((): void => {
		cancelScheduledClose();
		setIsOpen(false);
	}, [cancelScheduledClose]);

	const scheduleClose = useCallback((): void => {
		cancelScheduledClose();
		closeTimerRef.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
	}, [cancelScheduledClose]);

	/*
	 * Mouse pointers only. A tap synthesises `mouseenter` before `click`, so
	 * treating it as hover would open the menu and let the chevron's toggle
	 * immediately close it again — the control would look dead on a touchscreen
	 * wide enough to render this bar.
	 */
	const handlePointerEnter = useCallback(
		(event: PointerEvent<HTMLElement>): void => {
			if (event.pointerType === 'mouse') {
				open();
			}
		},
		[open],
	);

	const handlePointerLeave = useCallback(
		(event: PointerEvent<HTMLElement>): void => {
			if (event.pointerType === 'mouse') {
				scheduleClose();
			}
		},
		[scheduleClose],
	);

	useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

	return (
		/*
		 * `onFocus`/`onBlur` here rather than on the controls: React maps them to
		 * `focusin`/`focusout`, which bubble, so this behaves as focus-within —
		 * tabbing from the link to a menu item keeps the menu open, and leaving
		 * the item altogether closes it.
		 */
		<span
			css={styles.wrapper}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
			onFocus={open}
			onBlur={scheduleClose}
			data-testid={testId}
		>
			<Popup
				placement="bottom-start"
				isOpen={isOpen}
				onClose={close}
				// Keeps the popout inside the wrapper, which is what makes a pointer
				// moving from the label onto a menu item count as staying hovered
				// rather than leaving.
				shouldRenderToParent
				testId={`${testId}-popup`}
				content={() => (
					<MenuGroup minWidth={192} maxWidth={320} onClick={close}>
						<Section isList titleId={ANCHOR_MENU_HEADING_ID}>
							<HeadingItem id={ANCHOR_MENU_HEADING_ID}>
								<FormattedMessage {...messages.navSectionsHeading} />
							</HeadingItem>
							{anchors.map((item) => (
								<AnchorLinkItem key={item.token} {...item} />
							))}
						</Section>
					</MenuGroup>
				)}
				trigger={({ ref, ...ariaProps }: TriggerProps) => (
					/*
					 * The ref and the ARIA state are deliberately split: the ref goes
					 * on the row, so the popout aligns to the item's left edge, while
					 * the state goes on the chevron, which is the element that actually
					 * opens the menu and the one assistive tech should hear about. A
					 * `span` cannot carry `aria-expanded` meaningfully.
					 */
					<span ref={ref} css={[styles.row, isActive && styles.rowActive]}>
						{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics */}
						<a
							href={path}
							css={[styles.link, isActive && styles.linkActive]}
							aria-current={isActive ? 'page' : undefined}
							onClick={onClick}
							data-testid={`${testId}-link`}
						>
							<FormattedMessage {...message} />
						</a>
						<button
							{...ariaProps}
							type="button"
							css={styles.chevron}
							aria-label={formatMessage(messages.navSectionsLabel, {
								page: formatMessage(message),
							})}
							onClick={() => setIsOpen((previous) => !previous)}
							data-testid={`${testId}-trigger`}
						>
							<span css={[styles.chevronIcon, isOpen && styles.chevronIconOpen]}>
								<ChevronIcon testId={`${testId}-chevron`} />
							</span>
						</button>
					</span>
				)}
			/>
		</span>
	);
};
